/**
 * Refreshable web-console sessions (P4-07, architecture §B "Access").
 *
 * A sign-in made by the web console sends `X-Session-Refresh: 1` and gets a
 * 15-minute access token plus an opaque, rotating refresh token. This module
 * renews the pair transparently:
 *
 *   - proactively, before a request goes out with an access token that is
 *     about to expire (services/api.ts request interceptor);
 *   - reactively, once, when a request comes back 401 INVALID_TOKEN (response
 *     interceptor), then the request is retried with the new token.
 *
 * Refresh tokens are single-use and REUSE of a rotated one ends the whole
 * session (server-side reuse detection). Several tabs share one session
 * through localStorage, so a refresh must never present a token another tab
 * already rotated:
 *
 *   1. one refresh at a time per tab (a shared in-flight promise);
 *   2. one at a time across tabs (Web Locks API, `navigator.locks`);
 *   3. inside the lock, re-read the persisted session first: if another tab
 *      already rotated it, adopt that pair instead of refreshing;
 *   4. a 409 REFRESH_RACE from the server (two tabs without Web Locks) is
 *      answered the same way: adopt the newer persisted pair.
 *
 * A refusal ends the refreshable session at once: a terminal code
 * (TOKEN_REVOKED after a password change or sign-out-everywhere,
 * USER_DISABLED) goes through handleTerminalAuth like any other terminal
 * answer, and any other refusal drops the refresh token so the 401 that
 * follows signs out instead of presenting the dead token a second time.
 *
 * Native shells (device sessions with their own refresh) and the embedded
 * console (console handoff) never ask for a refreshable session.
 */
import { Capacitor } from '@capacitor/core';
import { isTokenExpired, useAuthStore } from '@/store/auth.store';
import { isEmbeddedMode } from '@/utils/embedded-console';
import { errorCodeOf, rawPost, type RawPost } from '@/services/raw-http';
import { handleTerminalAuth, terminalAuthCode } from '@/services/terminal-auth';

export const SESSION_REFRESH_HEADER = 'X-Session-Refresh';
const LOCK_NAME = 'cb-console-session-refresh';
const PERSIST_KEY = 'auth-storage';

/** Sign-in endpoints that answer with a session; only these carry the opt-in header. */
const SIGN_IN_PATHS = [
  '/auth/login',
  '/auth/magic-link/verify',
  '/auth/mfa/challenge',
  '/auth/passkeys/authentication/verify',
];

/** Paths a refresh must never be attempted for (they authenticate by other means). */
const NO_REFRESH_PATHS = [...SIGN_IN_PATHS, '/auth/session/', '/auth/console-handoff/', '/auth/passkeys/authentication/'];

export type RefreshOutcome = 'refreshed' | 'failed' | 'transient';

export interface ConsoleSessionTokens {
  token: string;
  refresh_token?: string;
}

/** Whether this client asks for (and keeps) a refreshable console session. */
export function wantsConsoleRefresh(): boolean {
  try {
    if (Capacitor.isNativePlatform()) return false;
  } catch {
    // not a Capacitor build: web
  }
  return !isEmbeddedMode();
}

export function isSignInPath(url: string | undefined): boolean {
  return !!url && SIGN_IN_PATHS.some((p) => url.includes(p));
}

export function isNoRefreshPath(url: string | undefined): boolean {
  return !!url && NO_REFRESH_PATHS.some((p) => url.includes(p));
}

/** The session another tab may have written: read straight from storage, never from memory. */
export function readPersistedSession(): { token: string | null; refreshToken: string | null } | null {
  try {
    const raw = window.localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const state = (JSON.parse(raw) as { state?: { token?: unknown; refreshToken?: unknown } }).state;
    if (!state) return null;
    return {
      token: typeof state.token === 'string' ? state.token : null,
      refreshToken: typeof state.refreshToken === 'string' ? state.refreshToken : null,
    };
  } catch {
    return null;
  }
}

/** Whether the in-memory access token should be renewed before it is sent. */
export function accessTokenNeedsRefresh(): boolean {
  const { token, refreshToken } = useAuthStore.getState();
  return !!token && !!refreshToken && isTokenExpired(token);
}

function adoptIfNewer(presented: string | null): boolean {
  const persisted = readPersistedSession();
  if (
    persisted?.token &&
    persisted.refreshToken &&
    persisted.refreshToken !== presented &&
    !isTokenExpired(persisted.token)
  ) {
    useAuthStore.getState().setToken(persisted.token, persisted.refreshToken);
    return true;
  }
  return false;
}

async function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined);
  if (locks && typeof locks.request === 'function') {
    return locks.request(LOCK_NAME, { mode: 'exclusive' }, fn) as Promise<T>;
  }
  return fn();
}

/** The family is over: forget its refresh token (memory and storage) so nothing presents it again. */
function dropRefreshToken(): void {
  try {
    useAuthStore.setState({ refreshToken: null });
  } catch {
    // the store is unavailable (tests): nothing persisted to drop
  }
}

let inFlight: Promise<RefreshOutcome> | null = null;
let post: RawPost = rawPost;

/** Test seam. */
export function __setRefreshTransportForTests(transport: RawPost | null): void {
  post = transport ?? rawPost;
  inFlight = null;
}

async function doRefresh(): Promise<RefreshOutcome> {
  const presented = useAuthStore.getState().refreshToken;
  // Another tab may have rotated the session while this one waited.
  if (adoptIfNewer(presented)) return 'refreshed';
  const refreshToken = readPersistedSession()?.refreshToken ?? presented;
  if (!refreshToken) return 'failed';

  let res;
  try {
    res = await post('/auth/session/refresh', { refresh_token: refreshToken }, {}, { timeoutMs: 15_000 });
  } catch {
    return 'transient'; // offline / timeout: keep the session, try again later
  }
  if (res.status === 200) {
    const data = res.data as ConsoleSessionTokens;
    if (typeof data?.token !== 'string' || typeof data.refresh_token !== 'string') return 'failed';
    useAuthStore.getState().setToken(data.token, data.refresh_token);
    return 'refreshed';
  }
  if (res.status === 409 && errorCodeOf(res.data) === 'REFRESH_RACE') {
    // Another tab won the rotation a moment ago; its pair is (or is about to be) persisted.
    await new Promise((r) => setTimeout(r, 250));
    return adoptIfNewer(refreshToken) ? 'refreshed' : 'transient';
  }
  if (res.status === 429 || res.status >= 500) return 'transient';
  const terminal = terminalAuthCode(res.status, res.data);
  dropRefreshToken();
  if (terminal) void handleTerminalAuth(terminal);
  return 'failed';
}

/**
 * Renew the console session. Single-flight per tab, serialised across tabs.
 * 'failed' means the session is over (sign in again); 'transient' means try
 * later and keep the session.
 */
export function refreshConsoleSession(): Promise<RefreshOutcome> {
  if (!useAuthStore.getState().refreshToken && !readPersistedSession()?.refreshToken) {
    return Promise.resolve('failed');
  }
  if (!inFlight) {
    inFlight = withCrossTabLock(doRefresh).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/**
 * Sign-out of THIS console: end its refresh family on the server (other
 * consoles and devices stay signed in). Best effort, bounded; never throws.
 */
export async function revokeConsoleSession(): Promise<void> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return;
  try {
    await post('/auth/session/revoke', { refresh_token: refreshToken }, {}, { timeoutMs: 3000 });
  } catch {
    // Offline: the family expires on its own; the local sign-out still happens.
  }
}
